/**
 * Quote Email Fetcher Service
 * Integrates with EmailIntegrationPanel functionality to fetch case emails for quote parsing
 * Filters and prepares emails for SupplierQuoteEmailParser processing
 */

interface CaseEmail {
  id: string;
  case_id: string;
  from_address: string;
  to_address: string;
  subject: string;
  body_text?: string;
  body_html?: string;
  received_at: string;
  message_id: string;
  is_outbound: boolean;
  reply_to_id?: string;
}

interface QuoteEmailCandidate extends CaseEmail {
  quoteLikelihood: 'high' | 'medium' | 'low';
  extractionPreview: {
    hasPositions: boolean;
    hasPricing: boolean;
    hasDeliveryInfo: boolean;
    supplierName?: string;
    positionCount: number;
  };
}

class QuoteEmailFetcher {
  private static readonly BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  /**
   * Fetch all emails for a case and filter potential quote responses
   */
  static async fetchQuoteEmailCandidates(caseId: string): Promise<QuoteEmailCandidate[]> {
    try {
      console.log(`📧 Fetching emails for case ${caseId} to find quote candidates...`);
      
      const response = await fetch(`${this.BASE_URL}/cases/${caseId}/emails`, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch case emails: ${response.statusText}`);
      }

      const emails: CaseEmail[] = await response.json();
      console.log(`📥 Retrieved ${emails.length} emails for case ${caseId}`);

      // Filter to incoming emails only (potential supplier responses)
      const incomingEmails = emails.filter(email => !email.is_outbound);
      console.log(`📨 Found ${incomingEmails.length} incoming emails`);

      // Analyze each email for quote likelihood
      const candidates: QuoteEmailCandidate[] = [];
      
      for (const email of incomingEmails) {
        const analysis = this.analyzeEmailForQuoteContent(email);
        if (analysis.quoteLikelihood !== 'low' || analysis.extractionPreview.hasPositions) {
          candidates.push({
            ...email,
            quoteLikelihood: analysis.quoteLikelihood,
            extractionPreview: analysis.extractionPreview
          });
        }
      }

      // Sort by likelihood and date (most recent high-likelihood first)
      candidates.sort((a, b) => {
        const likelihoodOrder = { 'high': 3, 'medium': 2, 'low': 1 };
        const aScore = likelihoodOrder[a.quoteLikelihood];
        const bScore = likelihoodOrder[b.quoteLikelihood];
        
        if (aScore !== bScore) {
          return bScore - aScore; // High likelihood first
        }
        
        return new Date(b.received_at).getTime() - new Date(a.received_at).getTime(); // Most recent first
      });

      console.log(`✅ Found ${candidates.length} quote email candidates:`, 
        candidates.map(c => `${c.quoteLikelihood} likelihood from ${c.from_address}`));

      return candidates;
    } catch (error) {
      console.error('❌ Error fetching quote email candidates:', error);
      throw error;
    }
  }

  /**
   * Analyze email content to determine quote likelihood
   */
  private static analyzeEmailForQuoteContent(email: CaseEmail): {
    quoteLikelihood: 'high' | 'medium' | 'low';
    extractionPreview: QuoteEmailCandidate['extractionPreview'];
  } {
    const content = (email.body_text || email.body_html || '').toLowerCase();
    const subject = (email.subject || '').toLowerCase();
    
    // Quote-related keywords
    const quoteKeywords = {
      high: ['angebot', 'quote', 'quotation', 'preisangabe', 'preis', 'kosten', 'cost'],
      medium: ['lieferzeit', 'delivery', 'verfügbar', 'available', 'price', 'payment'],
      low: ['information', 'anfrage', 'inquiry', 'frage', 'question']
    };

    // Check for price patterns
    const pricePatterns = [
      /€\s*[\d.,]+/g,
      /[\d.,]+\s*€/g,
      /\$\s*[\d.,]+/g,
      /[\d.,]+\s*\$/g,
      /preis[:\s]*[\d.,]+/gi,
      /price[:\s]*[\d.,]+/gi
    ];

    // Check for position/item patterns
    const positionPatterns = [
      /position\s*\d+/gi,
      /pos\.?\s*\d+/gi,
      /artikel\s*\d+/gi,
      /item\s*\d+/gi,
      /\d+\.\s*[a-zA-Z]/g // "1. Something", "2. Another"
    ];

    // Check for delivery patterns
    const deliveryPatterns = [
      /lieferzeit[\s:]*\d+/gi,
      /delivery[\s:]*\d+/gi,
      /\d+\s*(?:tage|days|wochen|weeks)/gi,
      /verfügbar[\s:]*\d+/gi,
      /available[\s:]*\d+/gi
    ];

    // Analyze content
    let quoteLikelihood: 'high' | 'medium' | 'low' = 'low';
    const hasPricing = pricePatterns.some(pattern => pattern.test(content + ' ' + subject));
    const hasPositions = positionPatterns.some(pattern => pattern.test(content + ' ' + subject));
    const hasDeliveryInfo = deliveryPatterns.some(pattern => pattern.test(content + ' ' + subject));
    
    // Count position matches for preview
    const positionMatches = content.match(/(?:position|pos\.?|artikel|item)\s*\d+/gi) || [];
    const positionCount = Math.max(positionMatches.length, 
      (content.match(/\d+\.\s*[a-zA-Z]/g) || []).length);

    // Extract supplier name from email address
    const supplierName = email.from_address.split('@')[0].replace(/[._-]/g, ' ');

    // Determine likelihood
    if (hasPricing && hasPositions) {
      quoteLikelihood = 'high';
    } else if (hasPricing || hasPositions || 
               quoteKeywords.high.some(keyword => content.includes(keyword) || subject.includes(keyword))) {
      quoteLikelihood = 'medium';
    } else if (quoteKeywords.medium.some(keyword => content.includes(keyword) || subject.includes(keyword))) {
      quoteLikelihood = 'medium';
    }

    // Special patterns that increase likelihood
    if (content.includes('anhang') || content.includes('attachment') || 
        content.includes('pdf') || content.includes('excel')) {
      if (quoteLikelihood === 'low') quoteLikelihood = 'medium';
      if (quoteLikelihood === 'medium') quoteLikelihood = 'high';
    }

    return {
      quoteLikelihood,
      extractionPreview: {
        hasPositions,
        hasPricing,
        hasDeliveryInfo,
        supplierName,
        positionCount
      }
    };
  }

  /**
   * Get a specific email by ID for parsing
   */
  static async fetchEmailById(emailId: string): Promise<CaseEmail | null> {
    try {
      console.log(`📧 Fetching email ${emailId} for parsing...`);
      
      const response = await fetch(`${this.BASE_URL}/emails/${emailId}`, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to fetch email: ${response.statusText}`);
      }

      const email: CaseEmail = await response.json();
      console.log(`✅ Retrieved email from ${email.from_address} with subject: ${email.subject}`);
      
      return email;
    } catch (error) {
      console.error('❌ Error fetching email by ID:', error);
      throw error;
    }
  }

  /**
   * Preview what data would be extracted from an email without full parsing
   */
  static previewEmailExtraction(email: CaseEmail): {
    supplierName: string;
    estimatedPositions: number;
    hasPricing: boolean;
    hasDeliveryInfo: boolean;
    confidence: 'high' | 'medium' | 'low';
  } {
    const content = email.body_text || email.body_html || '';
    
    // Quick analysis for preview
    const priceMatches = content.match(/€\s*[\d.,]+|[\d.,]+\s*€|\$\s*[\d.,]+|[\d.,]+\s*\$/g) || [];
    const positionMatches = content.match(/(?:position|pos\.?|artikel|item)\s*\d+/gi) || [];
    const deliveryMatches = content.match(/(?:lieferzeit|delivery)[\s:]*\d+/gi) || [];
    
    const supplierName = email.from_address.split('@')[0].replace(/[._-]/g, ' ');
    const estimatedPositions = Math.max(positionMatches.length, 
      (content.match(/\d+\.\s*[a-zA-Z]/g) || []).length);
    const hasPricing = priceMatches.length > 0;
    const hasDeliveryInfo = deliveryMatches.length > 0;
    
    let confidence: 'high' | 'medium' | 'low' = 'low';
    if (estimatedPositions > 0 && hasPricing) {
      confidence = 'high';
    } else if (estimatedPositions > 0 || hasPricing) {
      confidence = 'medium';
    }

    return {
      supplierName,
      estimatedPositions,
      hasPricing,
      hasDeliveryInfo,
      confidence
    };
  }

  /**
   * Format email preview for display in selection UI
   */
  static formatEmailPreview(candidate: QuoteEmailCandidate): {
    title: string;
    subtitle: string;
    preview: string;
    confidence: string;
    metadata: string[];
  } {
    const receivedDate = new Date(candidate.received_at).toLocaleDateString('de-DE');
    const receivedTime = new Date(candidate.received_at).toLocaleTimeString('de-DE', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });

    const metadata: string[] = [];
    if (candidate.extractionPreview.hasPositions) {
      metadata.push(`${candidate.extractionPreview.positionCount} Positionen`);
    }
    if (candidate.extractionPreview.hasPricing) {
      metadata.push('Preise erkannt');
    }
    if (candidate.extractionPreview.hasDeliveryInfo) {
      metadata.push('Lieferzeit erkannt');
    }

    const confidenceLabels = {
      high: '🟢 Hoch',
      medium: '🟡 Mittel', 
      low: '🔴 Niedrig'
    };

    // Create content preview (first 150 chars)
    const content = candidate.body_text || candidate.body_html || '';
    const cleanContent = content.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    const preview = cleanContent.length > 150 ? 
      cleanContent.substring(0, 150) + '...' : cleanContent;

    return {
      title: candidate.subject || 'Kein Betreff',
      subtitle: `Von: ${candidate.extractionPreview.supplierName || candidate.from_address}`,
      preview,
      confidence: confidenceLabels[candidate.quoteLikelihood],
      metadata: [`📅 ${receivedDate} ${receivedTime}`, ...metadata]
    };
  }
}

export { QuoteEmailFetcher, type CaseEmail, type QuoteEmailCandidate };
